import enUs from 'antd/es/locale/en_US';
import hrHr from 'antd/es/locale/hr_HR';
import srRs from 'antd/es/locale/sr_RS';
import i18n from 'i18next';
import moment from 'moment';
import 'moment/locale/bs';
import 'moment/locale/hr';
import 'moment/locale/sr';
import 'moment/locale/sr-cyrl';
import { initReactI18next, useTranslation, Trans } from 'react-i18next';
import bs from './locales/bs';
import { getConfigValue } from '../config/clients';

export const DEFAULT_LANGUAGE = 'bs';
export const LANGUAGE_STORAGE_KEY = 'msales.language';

// antd has no Serbian Cyrillic locale, and rc-picker passes the locale code from
// this object straight to moment, overriding the global one. Repointing the code
// keeps month and weekday names in the same script as the rest of the UI.
function withMomentLocale(value, momentLocale) {
  if (Array.isArray(value)) {
    return value.map((item) => withMomentLocale(item, momentLocale));
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const result = {};

  Object.keys(value).forEach((key) => {
    result[key] =
      key === 'locale' && typeof value[key] === 'string'
        ? momentLocale
        : withMomentLocale(value[key], momentLocale);
  });

  return result;
}

const srRsCyrl = withMomentLocale(srRs, 'sr-cyrl');

export const supportedLanguages = {
  bs: {
    label: 'Bosanski',
    momentLocale: 'bs',
    antdLocale: withMomentLocale(srRs, 'bs'),
  },
  en: {
    label: 'English',
    momentLocale: 'en',
    antdLocale: enUs,
  },
  hr: {
    label: 'Hrvatski',
    momentLocale: 'hr',
    antdLocale: hrHr,
  },
  sr: {
    label: 'Srpski (ćirilica)',
    momentLocale: 'sr-cyrl',
    antdLocale: srRsCyrl,
  },
  'sr-latn': {
    label: 'Srpski (latinica)',
    momentLocale: 'sr',
    antdLocale: srRs,
  },
};

const resources = {
  [DEFAULT_LANGUAGE]: {
    translation: bs,
  },
};

function safeReadLocalStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function safeWriteLocalStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Blocked storage only costs us persistence, so the session continues.
  }
}

export function getAllowedLanguages() {
  const configured = getConfigValue('localization.languages');
  const allowed = Array.isArray(configured)
    ? configured.filter((code) => supportedLanguages[code])
    : [];

  return allowed.length ? allowed : [DEFAULT_LANGUAGE];
}

function getDefaultLanguage() {
  const allowed = getAllowedLanguages();
  const override = process.env.REACT_APP_LOCALE_OVERRIDE;

  // An override still has to be a language this client offers, otherwise the
  // picker would show a code it has no option for.
  return normalizeLanguage(
    override && allowed.includes(override) ? override : allowed[0],
  );
}

function getStoredLanguage() {
  const storedLanguage = safeReadLocalStorage(LANGUAGE_STORAGE_KEY);

  // A language the client no longer offers must not survive in storage,
  // otherwise the device boots into a language its menu cannot show.
  return storedLanguage && getAllowedLanguages().includes(storedLanguage)
    ? storedLanguage
    : null;
}

let currentLanguage = getStoredLanguage() || getDefaultLanguage();

i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LANGUAGE,
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    escapeValue: false,
  },
  initImmediate: false,
  returnNull: false,
  // i18next would otherwise rewrite sr-latn to sr-Latn and never match our bundle.
  lowerCaseLng: true,
  // Keeps sr out of sr-latn's fallback chain, so a key missing from the Latin
  // catalogue falls back to Bosnian rather than rendering in the wrong script.
  load: 'currentOnly',
});
moment.locale(supportedLanguages[currentLanguage].momentLocale);

export function normalizeLanguage(language) {
  const normalized = String(language || DEFAULT_LANGUAGE)
    .trim()
    .toLowerCase()
    .replace('_', '-');

  // Script-tagged codes such as sr-latn are real keys, so match the full value
  // before falling back to the bare language subtag.
  if (supportedLanguages[normalized]) {
    return normalized;
  }

  const base = normalized.split('-')[0];

  return supportedLanguages[base] ? base : DEFAULT_LANGUAGE;
}

export function getLanguage() {
  return currentLanguage;
}

export function isLanguageLoaded(language = currentLanguage) {
  return i18n.hasResourceBundle(normalizeLanguage(language), 'translation');
}

export async function loadLanguageResources(language) {
  const normalizedLanguage = normalizeLanguage(language);

  if (isLanguageLoaded(normalizedLanguage)) {
    return normalizedLanguage;
  }

  // Lazy chunks, so the service worker precaches them for offline use.
  const translations = await import(
    /* webpackChunkName: "locale-[request]" */
    /* webpackExclude: /bs\.js$/ */
    `./locales/${normalizedLanguage}`
  );

  i18n.addResourceBundle(
    normalizedLanguage,
    'translation',
    translations.default,
    true,
    true,
  );

  return normalizedLanguage;
}

async function applyLanguage(language) {
  currentLanguage = normalizeLanguage(language);
  moment.locale(supportedLanguages[currentLanguage].momentLocale);
  await i18n.changeLanguage(currentLanguage);

  return currentLanguage;
}

export async function initializeLocalization() {
  try {
    await loadLanguageResources(currentLanguage);

    return await applyLanguage(currentLanguage);
  } catch (error) {
    console.error('Failed to initialize localization.', error);

    return applyLanguage(DEFAULT_LANGUAGE);
  }
}

export async function setLanguage(language) {
  const nextLanguage = normalizeLanguage(language);

  await loadLanguageResources(nextLanguage);
  await applyLanguage(nextLanguage);
  safeWriteLocalStorage(LANGUAGE_STORAGE_KEY, currentLanguage);

  return currentLanguage;
}

export function getAntdLocale(language = currentLanguage) {
  return supportedLanguages[normalizeLanguage(language)].antdLocale;
}

export function t(key, params, language = currentLanguage) {
  const normalizedLanguage = normalizeLanguage(language);

  return i18n.t(key, {
    ...params,
    lng: normalizedLanguage,
  });
}

export const localization = {
  getAllowedLanguages,
  getAntdLocale,
  getLanguage,
  i18n,
  initializeLocalization,
  isLanguageLoaded,
  loadLanguageResources,
  setLanguage,
  supportedLanguages,
  t,
  Trans,
  useTranslation,
};

export { i18n, useTranslation, Trans };

export default localization;
